import React, { useCallback, useState, useEffect, useRef, useMemo } from 'react';
import { IEditorProps } from '../Editor';
import { Label } from '../../inputs/Label';
import { getPropertyDisplayLabel, getChildValueAsString } from 'components/property-editors/utils/ProviderNodeUtils';
import { StringComboBox } from '../../inputs/StringComboBox';
import { TextField } from '../../inputs/TextField';
import {
  getPartsFromValue,
  getValueForFormatParts,
  getNumberFormatOptions,
  getDecimalPlaceOptions,
} from './utils/NumberFormatHelpers';
import './NumberFormatEditor.less';
import { getEditorDataAttributeProps } from '../utils/EditorDataAttributeHelpers';
import { useEditorId } from '../hooks/useEditorId';
import { debounce } from 'debounce';
import { getEditorConfigValue } from 'components/property-editors/utils/EditorConfigurationUtils';

interface INumberFormatEditorProps extends IEditorProps {}

/**
 * Number format editor.
 *
 * Copyright (c) 2020 Decisions All Rights Reserved
 */
export function NumberFormatEditor({ child, id, onChange, path }: INumberFormatEditorProps) {
  const value = getPartsFromValue(getChildValueAsString(child));
  const [editorId] = useEditorId(path, id);
  const dataAttrs = getEditorDataAttributeProps(child, path);
  // use "precision" for certain cases. e.g. when showing 10 million as "10m",
  // two significant digits doesn't mean two decimal places of 2
  const placesLabel = getEditorConfigValue(child, 'usePrecision') ? 'Precision' : 'Decimal Places';
  const showAppend = !getEditorConfigValue(child, 'hideAppend');
  const [format, setFormat] = useState(value.format);
  const [places, setPlaces] = useState(value.places);
  const [append, setAppend] = useState(value.append);
  const prevAppend = useRef(append);

  const onFormatChange = useCallback(
    (value: string | null) => {
      const format = value || '';
      setFormat(format);
      onChange(getValueForFormatParts(format, places, append), path);
    },
    [append, onChange, path, places]
  );
  const onDecimalChange = useCallback(
    (value: string | null) => {
      const places = value || '';
      setPlaces(places);
      onChange(getValueForFormatParts(format, places, append), path);
    },
    [append, format, onChange, path]
  );
  const onAppendChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newAppend = e.target.value;
    setAppend(newAppend);
  };

  // useCallback to preserve
  const debouncedEmitAppendChange = useMemo(
    () =>
      debounce((append: string) => {
        if (prevAppend.current !== append) {
          onChange(getValueForFormatParts(format, places, append), path);
        }
        prevAppend.current = append;
      }, 500),
    [format, onChange, path, places]
  );

  useEffect(() => {
    // debounce emitting when append changes
    debouncedEmitAppendChange.clear();
    debouncedEmitAppendChange(append);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [append]);

  const onAppendBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    // clear any debounced handlers
    debouncedEmitAppendChange.clear();

    if (prevAppend.current !== append) {
      // emit the current value
      onChange(getValueForFormatParts(format, places, e.target.value), path);
      prevAppend.current = e.target.value;
    }
  };

  return (
    <Label
      htmlFor={editorId}
      label={getPropertyDisplayLabel(child)}
      className="dpc-number-format-editor"
      {...dataAttrs}
    >
      <StringComboBox
        id={editorId}
        options={getNumberFormatOptions()}
        currentValue={format}
        onValueChange={onFormatChange}
        className="dpc-number-format-editor__first-field"
      />
      <Label label={placesLabel} htmlFor={`${editorId}_decimals`} className="dpc-number-format-editor__nested-field">
        <StringComboBox
          id={`${editorId}_decimals`}
          options={getDecimalPlaceOptions()}
          currentValue={places}
          onValueChange={onDecimalChange}
        />
      </Label>
      {showAppend && (
        <Label label="Append" htmlFor={`${editorId}_append`} className="dpc-number-format-editor__nested-field">
          <TextField value={append} onChange={onAppendChange} onBlur={onAppendBlur} />
        </Label>
      )}
    </Label>
  );
}
